import { Rule, WidgetFormConfigBase } from '@juvare/common-form-ui/WidgetForm/types';
import { WidgetFormModalRowListConfig } from '@juvare/common-form-ui/WidgetFormModalRowList';
import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { requireAnyOrgPermission, requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { requestLicenseCheckFunctionDef } from '@juvare/credentialing/backend/features/credentialing-verification/shared';
import { allowManageDriverLicense, allowRequestLicenseCheck } from '@juvare/credentialing/backend/features/permissions';
import { UserState } from '@juvare/platform-sdk-react/types';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import React from 'react';
import { useTranslation } from 'react-i18next';

export function useRmsFormRules(userState?: UserState) {
    const { t } = useTranslation();
    const requestLicenseCheck = useTypedPlatformFunction(requestLicenseCheckFunctionDef);

    return React.useMemo(
        () => [
            {
                type: 'showOnlyForSelfAndManageAccounts',
                label: t('rms:rules.showOnlyForSelfAndManageAccounts'),
                applyRule: (config, _data, formParams) => {
                    config.disabled = !allowManageDriverLicense(userState, formParams?.id);
                },
            } satisfies Rule<
                WidgetFormConfigBase & {
                    disabled?: boolean;
                }
            >,
            {
                type: 'showOnlyForAccountAdministrators',
                label: t('rms:rules.showOnlyForAccountAdministrators'),
                applyRule: (config) => {
                    const isAdministrator =
                        requireAnySystemPermission(userState, 'manage-accounts') || requireAnyOrgPermission(userState, 'manage-members');

                    if (!isAdministrator) {
                        config.hidden = true;
                    }
                },
            } satisfies Rule,
            {
                type: 'showLicenceCheckButton',
                label: t('rms:rules.showLicenceCheckButton'),
                applyRule: (config: WidgetFormModalRowListConfig) => {
                    if (config?.type !== 'form.modalRowList') {
                        return;
                    }

                    config.extension = {
                        getActions: ({ formParams, formData }) => {
                            const isAccountActive = formData.systemStatus === 'active';

                            if (!formParams?.id || !allowRequestLicenseCheck(userState) || !isAccountActive) {
                                return [];
                            }

                            return [
                                {
                                    icon: <ContentPasteOutlinedIcon />,
                                    label: t('credentialing.labels.verifyCredentials'),
                                    onClick: () => {
                                        if (userState) {
                                            showConfirmDialog({
                                                title: t('credentialing.titles.verifyCredentials'),
                                                onConfirm: () => {
                                                    requestLicenseCheck({
                                                        personId: formParams.id,
                                                        requestedBy: userState.displayName,
                                                    })
                                                        .then(() => {
                                                            showInfoSnackbar({
                                                                message: t('credentialing.messages.licenseCheckRequested'),
                                                            });
                                                        })
                                                        .catch(handleErrorDialog);
                                                },
                                            });
                                        }
                                    },
                                },
                            ];
                        },
                    };
                },
            } satisfies Rule<WidgetFormModalRowListConfig>,
            {
                type: 'manageBackgroundCheckConsent',
                label: t('rms:rules.manageBackgroundCheckConsent'),
                applyRule: (config, _data, formParams) => {
                    const isUpdatingOwnConsent = formParams?.page === 'profile' && formParams.id === userState?.id;
                    const hasRequestBackgroundCheckPermission = requireAnySystemPermission(userState, 'request-background-check');

                    const canUpdateConsent = isUpdatingOwnConsent || hasRequestBackgroundCheckPermission;

                    config.disabled = !canUpdateConsent;
                },
            } satisfies Rule<{ disabled: boolean } & WidgetFormConfigBase>,
            {
                type: 'fieldIsNotRequiredIfDisabled',
                label: t('rms:rules.fieldIsNotRequiredIfDisabled'),
                applyRule: (config) => {
                    if (config.disabled) {
                        config.validate.required = false;
                    }
                },
            } satisfies Rule<{ disabled: boolean; validate: { required: boolean } } & WidgetFormConfigBase>,
        ],
        [t, userState, requestLicenseCheck],
    );
}
